import React, { memo, useMemo } from 'react';
import { CalendarDays } from 'lucide-react';
import { getDaysInMonth, formatDate, isCurrentDay, isCurrentMonth, getMonthName } from '../utils/dateUtils';
import { TimeEntry } from '../types/database';
import { Card, CardBody, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { PeriodStepper } from './ui/period-stepper';
import { CalendarDay } from './CalendarDay';

interface CalendarProps {
  currentDate: Date;
  onDateSelect: (date: Date) => void;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onToday?: () => void;
  timeEntries: TimeEntry[];
  selectedDate?: Date | null;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const Calendar: React.FC<CalendarProps> = memo(({
  currentDate,
  onDateSelect,
  onPreviousMonth,
  onNextMonth,
  onToday,
  timeEntries,
  selectedDate
}) => {
  const daysInMonth = useMemo(() => getDaysInMonth(currentDate), [currentDate]);
  const monthName = useMemo(() => getMonthName(currentDate), [currentDate]);

  // Monday-first week: 0=Sunday becomes 6, 1=Monday becomes 0, etc.
  const dayOfWeek = daysInMonth[0].getDay();
  const leadingBlanks = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  const timeEntriesMap = useMemo(() => {
    const map = new Map<string, TimeEntry>();
    timeEntries.forEach(entry => map.set(entry.date, entry));
    return map;
  }, [timeEntries]);

  const totalHours = useMemo(
    () => timeEntries.reduce((sum, entry) => sum + (entry.totalHours || 0), 0),
    [timeEntries]
  );

  const selectedKey = selectedDate ? formatDate(selectedDate) : null;
  const viewingCurrentMonth = isCurrentMonth(currentDate);

  return (
    <Card>
      <CardHeader
        // Keep the controls on the title row so they never jump to a new line
        className="flex-nowrap px-3.5 sm:px-5"
        icon={<CalendarDays />}
        iconClassName="max-sm:hidden"
        title={monthName}
        description={
          timeEntries.length > 0
            ? `${timeEntries.length} ${timeEntries.length === 1 ? 'day' : 'days'} · ${totalHours.toFixed(1)}h logged`
            : 'No hours logged yet'
        }
        actions={
          <>
            {onToday && (
              // Always rendered (just hidden on the current month) so the arrows never shift position
              <Button
                variant="ghost"
                size="sm"
                onClick={onToday}
                disabled={viewingCurrentMonth}
                aria-hidden={viewingCurrentMonth}
                tabIndex={viewingCurrentMonth ? -1 : undefined}
                className={viewingCurrentMonth ? 'invisible' : undefined}
              >
                Today
              </Button>
            )}
            <PeriodStepper
              onPrevious={onPreviousMonth}
              onNext={onNextMonth}
              previousLabel="Previous month"
              nextLabel="Next month"
            />
          </>
        }
      />

      <CardBody className="px-2.5 sm:px-5">
        <div className="mb-1.5 grid grid-cols-7 gap-1 sm:gap-1.5">
          {WEEKDAYS.map((day, i) => (
            <div
              key={day}
              className={`py-1 text-center text-[11px] font-medium tracking-wide uppercase 2xl:text-xs ${i >= 5 ? 'text-fg-subtle/70' : 'text-fg-subtle'}`}
            >
              <span className="sm:hidden">{day.charAt(0)}</span>
              <span className="hidden sm:inline">{day}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
          {Array.from({ length: leadingBlanks }, (_, i) => (
            <div key={`blank-${i}`} aria-hidden />
          ))}

          {daysInMonth.map((date, i) => {
            const key = formatDate(date);
            const weekdayIndex = (leadingBlanks + i) % 7;
            return (
              <CalendarDay
                key={key}
                date={date}
                entry={timeEntriesMap.get(key)}
                isToday={isCurrentDay(date)}
                isSelected={key === selectedKey}
                isWeekend={weekdayIndex >= 5}
                onSelect={onDateSelect}
              />
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
});
